import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, increment, Timestamp, arrayUnion } from 'firebase/firestore';
import { db, auth } from './firebase';
import './AddBulkFees.css';

// Helper function to calculate which terms student has attended (for display purposes)
const calculateTermsAttended = (studentStartDate, termStructure) => {
  if (!termStructure || !termStructure.termDates) return [];
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = studentStartDate.toDate ? studentStartDate.toDate() : new Date(studentStartDate);
  startDate.setHours(0, 0, 0, 0);
  
  const currentYear = today.getFullYear();
  const termsAttended = [];
  
  for (let i = 1; i <= 4; i++) {
    const termData = termStructure.termDates[`term${i}`];
    if (!termData || !termData.start) continue;
    
    const [startMonth, startDay] = termData.start.split('-').map(Number);
    const [endMonth, endDay] = termData.end.split('-').map(Number);
    
    const termStart = new Date(currentYear, startMonth - 1, startDay);
    termStart.setHours(0, 0, 0, 0);
    
    const termEnd = new Date(currentYear, endMonth - 1, endDay);
    termEnd.setHours(23, 59, 59, 999);
    
    // Student attended if started before term ended AND today >= term start
    if (startDate <= termEnd && today >= termStart) {
      termsAttended.push(i);
    }
  }
  
  return termsAttended;
};

// Helper function to get ALL terms a student can pay for (allows paying ahead)
const getPayableTerms = (studentStartDate, termStructure) => {
  if (!termStructure || !termStructure.termDates) return [];
  
  const startDate = studentStartDate.toDate ? studentStartDate.toDate() : new Date(studentStartDate);
  startDate.setHours(0, 0, 0, 0);
  
  const currentYear = new Date().getFullYear();
  const payableTerms = [];
  
  for (let i = 1; i <= 4; i++) {
    const termData = termStructure.termDates[`term${i}`];
    if (!termData || !termData.start) continue;
    
    const [endMonth, endDay] = termData.end.split('-').map(Number);
    
    const termEnd = new Date(currentYear, endMonth - 1, endDay);
    termEnd.setHours(23, 59, 59, 999);
    
    // Student can pay for ANY term they're enrolled in (started before term ends)
    // This allows paying ahead for future terms
    if (startDate <= termEnd) {
      payableTerms.push(i);
    }
  }
  
  return payableTerms;
};

// Helper function to allocate payment across admin and terms
const allocatePayment = (student, adminAmount, termAmount, termStructure) => {
  const allocations = [];
  let remaining = termAmount;
  
  // STEP 1: Admin fee (separate, never auto-allocates)
  if (adminAmount > 0) {
    const currentAdminPaid = student.termPayments?.admin || 0;
    const adminFee = student.adminFee || 0;
    
    // Only validate if there's an admin fee to pay
    if (adminFee > 0) {
      // Check if admin already fully paid
      if (currentAdminPaid >= adminFee) {
        throw new Error(`Admin fee already paid (R${currentAdminPaid.toFixed(2)})`);
      }
      
      // Check if this payment would overpay admin
      if (currentAdminPaid + adminAmount > adminFee) {
        throw new Error(
          `Admin fee overpayment not allowed.\n` +
          `Already paid: R${currentAdminPaid.toFixed(2)}\n` +
          `Admin fee: R${adminFee.toFixed(2)}\n` +
          `Maximum you can pay: R${(adminFee - currentAdminPaid).toFixed(2)}`
        );
      }
    } else {
      // No admin fee set for this student
      throw new Error(`No admin fee set for this student. Cannot allocate R${adminAmount.toFixed(2)} to admin fee.`);
    }
    
    allocations.push({ target: 'admin', amount: adminAmount });
  }
  
  // STEP 2: Term fees (auto-allocate in order) - ALLOW PAYING AHEAD
  if (remaining > 0) {
    const payableTerms = getPayableTerms(student.startDate, termStructure);
    const standardFee = student.feeStructure?.standardFee || 0;
    
    for (const termNum of payableTerms) {
      if (remaining <= 0) break;
      
      const termKey = `term${termNum}`;
      const alreadyPaid = student.termPayments?.[termKey] || 0;
      
      // Check for fee override for this specific term
      let termFee = standardFee;
      if (student.feeOverrides && Array.isArray(student.feeOverrides)) {
        const override = student.feeOverrides.find(
          o => o.termNumber === termNum && 
          (o.overrideType === 'term' || o.overrideType === 'both')
        );
        if (override && override.termFee !== undefined) {
          termFee = override.termFee;
        }
      }
      
      const stillOwes = termFee - alreadyPaid;
      
      if (stillOwes > 0) {
        const amountForThisTerm = Math.min(remaining, stillOwes);
        allocations.push({ target: termKey, amount: amountForThisTerm });
        remaining -= amountForThisTerm;
      }
    }
    
    // STEP 3: Credit (if payment exceeds all term fees)
    if (remaining > 0) {
      allocations.push({ target: 'credit', amount: remaining });
    }
  }
  
  return allocations;
};

function AddBulkFees({ onClose }) {
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState({});
  const [searchQuery, setSearchQuery] = useState(''); // For filtering within selected school
  const [globalSearchQuery, setGlobalSearchQuery] = useState(''); // NEW: For global search across all schools
  const [expandedRows, setExpandedRows] = useState({}); // NEW: Track which rows are expanded
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]); // Payment date for all entries

  useEffect(() => {
    loadSchools();
  }, []);

  // Filter students based on search query (within selected school)
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setFilteredStudents(students);
      return;
    }

    const searchLower = searchQuery.toLowerCase().trim();
    const filtered = students.filter(student => {
      const studentName = (student.studentName || '').toLowerCase();
      const accountNumber = (student.accountNumber || '').toLowerCase();
      return studentName.includes(searchLower) || accountNumber.includes(searchLower);
    });

    setFilteredStudents(filtered);
  }, [students, searchQuery]);

  // NEW: Global search across all schools when typing at the top
  useEffect(() => {
    if (globalSearchQuery.length >= 2) {
      loadAllStudents();
    } else if (globalSearchQuery.length < 2 && !selectedSchool) {
      // Clear students if global search is cleared and no school selected
      setStudents([]);
      setFilteredStudents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSearchQuery]);

  // Calculate TERM FEES owing (excluding admin) - using new structure
  const calculateTermFeesOwing = (student, termStructure) => {
    if (!student) return 0;
    
    // Use balance field directly (negative = owing)
    const balance = student.balance || 0;
    
    // If balance is negative, student owes money
    if (balance < 0) {
      // Subtract admin owing to get just term owing
      const adminOwing = calculateAdminFeeOwing(student);
      const termOwing = Math.abs(balance) - adminOwing;
      return Math.round(Math.max(0, termOwing) * 100) / 100;
    }
    
    // Balance is zero or positive (paid up or credit)
    return 0;
  };

  // Calculate ADMIN FEE owing separately
  const calculateAdminFeeOwing = (student) => {
    const feeOverrides = student.feeOverrides || [];
    
    const adminOverride = feeOverrides.find(
      o => o.overrideType === 'admin' || o.overrideType === 'both'
    );

    let adminFee;
    if (adminOverride && adminOverride.adminFee !== undefined) {
      adminFee = adminOverride.adminFee;
    } else {
      adminFee = student.adminFee || 0;
    }

    const adminFeePaid = student.termPayments?.admin || 0;
    return Math.round(Math.max(0, adminFee - adminFeePaid) * 100) / 100;
  };

  // NEW: Calculate term-by-term breakdown
  const calculateTermBreakdown = (student, termStructure) => {
    if (!student || !termStructure || !termStructure.termDates) {
      return [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const studentStartDate = student.startDate?.toDate 
      ? student.startDate.toDate() 
      : new Date(student.startDate);
    studentStartDate.setHours(0, 0, 0, 0);
    
    const currentYear = today.getFullYear();
    const termFee = student.feeStructure?.standardFee || 0;
    const feeOverrides = student.feeOverrides || [];
    
    const breakdown = [];
    let totalPaidSoFar = student.termFeesPaid || 0;

    for (let termNum = 1; termNum <= 4; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;

      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);
      
      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);
      
      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);

      const shouldCharge = (
        studentStartDate <= termEnd &&
        today >= termStart &&
        (student.isActive || 
          (student.deactivationInfo?.deactivatedDate && 
            new Date(student.deactivationInfo.deactivatedDate) > termStart))
      );

      // Get term fee (with override if applicable)
      const override = feeOverrides.find(
        o => o.termNumber === termNum && 
        (o.overrideType === 'term' || o.overrideType === 'both')
      );
      const termAmount = (override && override.termFee !== undefined) ? override.termFee : termFee;

      // Determine payment status for this term
      const termKey = `term${termNum}`;
      const paid = student.termPayments?.[termKey] || 0;
      let status = 'not-due';
      
      if (shouldCharge) {
        if (paid >= termAmount) {
          status = 'paid';
        } else if (paid > 0) {
          status = 'partial';
        } else {
          status = 'due';
        }
      }

      breakdown.push({
        termNumber: termNum,
        termAmount: termAmount,
        paid: paid,
        due: shouldCharge ? Math.max(0, termAmount - paid) : 0,
        status: status,
        startDate: termStart,
        endDate: termEnd
      });
    }

    return breakdown;
  };

  const toggleRowExpansion = (studentId) => {
    setExpandedRows(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const loadSchools = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      const q = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      const schoolsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      schoolsList.sort((a, b) => a.schoolName.localeCompare(b.schoolName));
      setSchools(schoolsList);
      setLoading(false);
    } catch (error) {
      console.error('Error loading schools:', error);
      alert('Error loading schools');
      setLoading(false);
    }
  };

  // NEW: Load all students across all schools for global search
  const loadAllStudents = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // Load ALL students from all schools
      const q = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);

      // FILTER students by globalSearchQuery first
      const searchLower = globalSearchQuery.toLowerCase().trim();
      const matchingDocs = snapshot.docs.filter(docSnap => {
        const data = docSnap.data();
        const studentName = (data.studentName || '').toLowerCase();
        const accountNumber = (data.accountNumber || '').toLowerCase();
        return studentName.includes(searchLower) || accountNumber.includes(searchLower);
      });

      // Get all term structures we might need
      const termStructureCache = {};
      
      const studentsPromises = matchingDocs.map(async (docSnap) => {
        const studentData = { id: docSnap.id, ...docSnap.data() };
        
        // Load school info
        if (studentData.schoolRef) {
          try {
            const schoolDoc = await getDoc(studentData.schoolRef);
            if (schoolDoc.exists()) {
              const schoolData = schoolDoc.data();
              studentData.schoolName = schoolData.schoolName;
              
              // Get term structure for this school
              let termStructure = null;
              if (schoolData.termStructureRef) {
                const termStructureId = schoolData.termStructureRef.id;
                
                // Check cache first
                if (!termStructureCache[termStructureId]) {
                  const termStructureDoc = await getDoc(schoolData.termStructureRef);
                  if (termStructureDoc.exists()) {
                    termStructureCache[termStructureId] = termStructureDoc.data();
                  }
                }
                termStructure = termStructureCache[termStructureId];
              }

              // Calculate owing amounts
              const termFeesOwing = termStructure 
                ? calculateTermFeesOwing(studentData, termStructure)
                : (studentData.termFeesOwing || 0);
              
              const adminFeeOwing = calculateAdminFeeOwing(studentData);

              studentData.termFeesOwing = termFeesOwing;
              studentData.adminFeeOwing = adminFeeOwing;
              studentData.calculatedOwing = termFeesOwing + adminFeeOwing;
              studentData.termStructure = termStructure; // Store for breakdown
            }
          } catch (err) {
            console.error('Error loading school for student:', err);
            studentData.schoolName = 'Unknown School';
          }
        } else {
          studentData.schoolName = 'No School';
        }

        return studentData;
      });

      const studentsList = await Promise.all(studentsPromises);
      studentsList.sort((a, b) => a.studentName.localeCompare(b.studentName));

      setStudents(studentsList);
      setFilteredStudents(studentsList);
      setPayments({});
      setLoading(false);
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Error loading students');
      setLoading(false);
    }
  };

  const loadStudents = async (schoolId) => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const schoolRef = doc(db, 'schools', schoolId);

      // Load ALL students from this school
      const q = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('schoolRef', '==', schoolRef)
      );

      const snapshot = await getDocs(q);

      // Get school and term structure for calculations
      const schoolDoc = await getDoc(schoolRef);
      const schoolData = schoolDoc.data();
      let termStructure = null;

      if (schoolData.termStructureRef) {
        const termStructureDoc = await getDoc(schoolData.termStructureRef);
        if (termStructureDoc.exists()) {
          termStructure = termStructureDoc.data();
        }
      }

      // Calculate owing for each student
      const studentsList = snapshot.docs.map(doc => {
        const studentData = { id: doc.id, ...doc.data() };
        
        const termFeesOwing = termStructure 
          ? calculateTermFeesOwing(studentData, termStructure)
          : (studentData.termFeesOwing || 0);
        
        const adminFeeOwing = calculateAdminFeeOwing(studentData);

        return {
          ...studentData,
          termFeesOwing,
          adminFeeOwing,
          calculatedOwing: termFeesOwing + adminFeeOwing,
          termStructure: termStructure // Store for breakdown
        };
      });

      // Sort by name
      studentsList.sort((a, b) => a.studentName.localeCompare(b.studentName));

      setStudents(studentsList);
      setFilteredStudents(studentsList);
      setPayments({}); // Reset payments when switching schools
      setSearchQuery(''); // Reset search when switching schools
      setLoading(false);
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Error loading students');
      setLoading(false);
    }
  };

  const handleSchoolChange = (e) => {
    const schoolId = e.target.value;
    
    if (!schoolId) {
      setSelectedSchool(null);
      setStudents([]);
      setFilteredStudents([]);
      setSearchQuery('');
      return;
    }

    // School selected - load students for that school
    const school = schools.find(s => s.id === schoolId);
    setSelectedSchool(school);
    setGlobalSearchQuery(''); // Clear global search when selecting a school
    loadStudents(schoolId);
  };

  const handlePaymentChange = (studentId, field, value) => {
  const student = students.find(s => s.id === studentId);
  if (!student) return;

  // For payment method, just update without validation
  if (field === 'paymentMethod') {
    setPayments(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
    return;
  }

  // Convert to number and round to 2 decimal places to avoid floating point issues
  const amount = Math.round(parseFloat(value || 0) * 100) / 100;

  // Validate admin fee - warn but don't block if overpaying
  // The allocatePayment function will properly handle and throw an error if needed
  if (field === 'adminFee' && amount > 0) {
    const maxAdminAllowed = Math.round(student.adminFeeOwing * 100) / 100;
    if (amount > maxAdminAllowed + 0.01) { // Allow 1 cent tolerance
      // Just a warning, but still allow the value
      console.warn(`Admin fee warning: Entering R${amount.toFixed(2)} but student only owes R${maxAdminAllowed.toFixed(2)}`);
    }
  }

  // REMOVED: No validation limit for term fees - parents can pay in advance for future terms!

  setPayments(prev => ({
    ...prev,
    [studentId]: {
      ...prev[studentId],
      [field]: value
    }
  }));
};

  const handleSaveAll = async () => {
    const paymentsToProcess = Object.entries(payments).filter(([studentId, payment]) => {
      const adminAmount = parseFloat(payment?.adminFee || 0);
      const termAmount = parseFloat(payment?.termFee || 0);
      return adminAmount > 0 || termAmount > 0;
    });

    if (paymentsToProcess.length === 0) {
      alert('No payments entered. Please enter at least one amount.');
      return;
    }

    if (!window.confirm(`You are about to record ${paymentsToProcess.length} payment(s). Continue?`)) {
      return;
    }

    setSaving(true);

    try {
      const user = auth.currentUser;
      const selectedPaymentDate = paymentDate; // Use the selected payment date

      let successCount = 0;
      let errorCount = 0;

      for (const [studentId, payment] of paymentsToProcess) {
        try {
          // Round to 2 decimal places to avoid floating point precision issues
          const adminAmount = Math.round(parseFloat(payment.adminFee || 0) * 100) / 100;
          const termAmount = Math.round(parseFloat(payment.termFee || 0) * 100) / 100;
          const totalAmount = Math.round((adminAmount + termAmount) * 100) / 100;

          const student = students.find(s => s.id === studentId);
          const paymentMethod = payment.paymentMethod || 'EFT';

          // Get term structure for allocation
          const schoolDoc = await getDoc(student.schoolRef);
          const schoolData = schoolDoc.data();
          let termStructure = null;
          if (schoolData.termStructureRef) {
            const termStructureDoc = await getDoc(schoolData.termStructureRef);
            if (termStructureDoc.exists()) {
              termStructure = termStructureDoc.data();
            }
          }

          // Calculate payment allocations
          let allocations;
          try {
            allocations = allocatePayment(student, adminAmount, termAmount, termStructure);
          } catch (error) {
            // Show error and skip this student
            console.error(`Allocation error for ${student.studentName}:`, error.message);
            alert(`${student.studentName}: ${error.message}`);
            errorCount++;
            continue;
          }

          // Build update object
          const updates = {};
          
          // Apply allocations
          for (const allocation of allocations) {
            if (allocation.target === 'admin') {
              updates['termPayments.admin'] = increment(allocation.amount);
            } else if (allocation.target === 'credit') {
              updates.creditBalance = increment(allocation.amount);
            } else {
              // term1, term2, term3, or term4
              updates[`termPayments.${allocation.target}`] = increment(allocation.amount);
            }
          }
          
          // Update totals
          updates.totalPaid = increment(totalAmount);
          updates.lastPaymentDate = new Date(selectedPaymentDate);
          updates.updatedAt = new Date();
          
          // Recalculate balance
          const newTotalPaid = (student.totalPaid || 0) + totalAmount;
          const totalExpected = student.totalExpected || 0;
          updates.balance = newTotalPaid - totalExpected;
          
          // Update payment status
          if (updates.balance >= 0) {
            updates.paymentStatus = 'paid';
          } else if (updates.balance < 0 && newTotalPaid > 0) {
            updates.paymentStatus = 'partial';
          } else {
            updates.paymentStatus = 'outstanding';
          }
          
          // Add to payment history
          const paymentRecord = {
            date: Timestamp.fromDate(new Date(selectedPaymentDate)),
            adminAmount: adminAmount,
            termAmount: termAmount,
            method: paymentMethod,
            reference: '',
            recordedBy: user.uid,
            recordedAt: Timestamp.now()
          };
          
          // Add allocation info
          if (allocations.length === 1) {
            paymentRecord.allocatedTo = allocations[0].target;
          } else {
            paymentRecord.allocations = allocations;
          }
          
          updates.paymentHistory = arrayUnion(paymentRecord);

          // Create payment record in payments collection (audit trail)
          const paymentData = {
            studentRef: doc(db, 'students', studentId),
            studentName: student.studentName,
            accountNumber: student.accountNumber,
            schoolRef: student.schoolRef,
            schoolName: student.schoolName,
            franchiseeRef: student.franchiseeRef,
            paymentDate: new Date(selectedPaymentDate),
            paymentMethod: paymentMethod,
            totalAmount: totalAmount,
            termFeeAmount: termAmount,
            adminFeeAmount: adminAmount,
            allocations: allocations,
            notes: 'Bulk fee entry',
            recordedBy: user.uid,
            recordedAt: new Date()
          };

          await addDoc(collection(db, 'payments'), paymentData);

          // Update student document
          await updateDoc(doc(db, 'students', studentId), updates);

          successCount++;
        } catch (error) {
          console.error('Error processing payment for student:', studentId, error);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        alert(`${'\u2705'} Success! ${successCount} payment(s) recorded.`);
      } else {
        alert(`${'\u26A0\uFE0F'} Processed ${successCount} payment(s) successfully.\n${errorCount} payment(s) failed.`);
      }

      // Reload students to show updated amounts
      if (selectedSchool) {
        await loadStudents(selectedSchool.id);
      }
    } catch (error) {
      console.error('Error saving payments:', error);
      alert('Error saving payments. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  if (loading && schools.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="bulk-fees-container">
      <div className="bulk-fees-header">
        <h1>{'\u{1F4B0}'} Add Fees</h1>
      </div>

      <div className="school-selector">
        <label>{'\u{1F50D}'} Quick Search Across All Schools:</label>
        <input
          type="text"
          className="global-search-input"
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #3498db',
            borderRadius: '5px',
            fontSize: '16px',
            marginBottom: '10px'
          }}
          placeholder="Type at least 2 letters to search any student by name or account number..."
          value={globalSearchQuery}
          onChange={(e) => {
            setGlobalSearchQuery(e.target.value);
            if (e.target.value.length >= 2) {
              setSelectedSchool(null);
              setSearchQuery('');
            }
          }}
        />
        {globalSearchQuery.length < 2 && !selectedSchool && (
          <div className="search-info" style={{background: '#e3f2fd', color: '#1976d2', marginBottom: '20px'}}>
            {'\u{1F4A1}'} Type 2+ letters to find any student across all your schools
          </div>
        )}
        {globalSearchQuery.length >= 2 && filteredStudents.length > 0 && (
          <div className="search-info" style={{marginBottom: '20px'}}>
            {'\u2705'} Found {filteredStudents.length} student(s) across all schools
          </div>
        )}
        {globalSearchQuery.length >= 2 && filteredStudents.length === 0 && !loading && (
          <div className="search-info" style={{background: '#fee', color: '#c00', marginBottom: '20px'}}>
            {'\u274C'} No students found matching "{globalSearchQuery}"
          </div>
        )}
      </div>

      <div className="school-selector">
        <label>Or Select Specific School:</label>
        <select
          value={selectedSchool?.id || ''}
          onChange={handleSchoolChange}
          className="school-select"
        >
          <option value="">-- Choose a school --</option>
          {schools.map(school => (
            <option key={school.id} value={school.id}>
              {school.schoolName}
            </option>
          ))}
        </select>
      </div>

      {selectedSchool && (
        <div className="student-search-section">
          <label>Search Students (in {selectedSchool.schoolName}):</label>
          <input
            type="text"
            className="student-search-input"
            placeholder="Type at least 2 letters to search by name or account number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.length >= 2 && (
            <div className="search-info">
              Showing {filteredStudents.length} of {students.length} students
            </div>
          )}
        </div>
      )}

      {loading && (selectedSchool || globalSearchQuery.length >= 2) && (
        <div className="loading-students">Loading students...</div>
      )}

      {!loading && selectedSchool && students.length === 0 && (
        <div className="no-students">
          <p>No students found for this school.</p>
        </div>
      )}

      {!loading && selectedSchool && students.length > 0 && filteredStudents.length === 0 && (
        <div className="no-students">
          <p>No students found matching "{searchQuery}"</p>
          <p style={{fontSize: '14px', color: '#999', marginTop: '10px'}}>
            Try typing a different name or account number
          </p>
        </div>
      )}

      {!loading && filteredStudents.length > 0 && (
        <>
          <div className="students-table-wrapper">
            <table className="bulk-fees-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  {globalSearchQuery.length >= 2 && <th>School</th>}
                  <th>Start Date</th>
                  <th>Outstanding</th>
                  <th>Last Payment</th>
                  <th>Payment Method</th>
                  <th>Admin Fee Received</th>
                  <th>Term Fee Received</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map(student => (
                  <React.Fragment key={student.id}>
                    <tr className={!student.isActive ? 'inactive-row' : ''}>
                      <td>
  <div style={{display: 'flex', alignItems: 'center'}}>
    <button 
      onClick={() => toggleRowExpansion(student.id)}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '12px',
        padding: '0 4px 0 0',
        color: '#3498db'
      }}
      title="Show term breakdown"
    >
      {expandedRows[student.id] ? '\u25BC' : '\u25B6'}
    </button>
    <span>
      {student.studentName}
                            {!student.isActive && (
                              <span className="inactive-badge">{'\u{1F4A4}'} Inactive</span>
                            )}
                          </span>
                        </div>
                      </td>
                      {globalSearchQuery.length >= 2 && (
                        <td style={{fontWeight: '500', color: '#3498db'}}>
                          {student.schoolName || 'No School'}
                        </td>
                      )}
                      <td>{formatDate(student.startDate)}</td>
                      <td className="amount-cell">
                        {formatCurrency(student.calculatedOwing)}
                      </td>
                      <td>{formatDate(student.lastPaymentDate)}</td>
                      <td>
                        <select
                          value={payments[student.id]?.paymentMethod || 'EFT'}
                          onChange={(e) => handlePaymentChange(student.id, 'paymentMethod', e.target.value)}
                          className="payment-method-select"
                        >
                          <option value="EFT">EFT</option>
                          <option value="Cash">Cash</option>
                          <option value="Card">Card</option>
                          <option value="Cheque">Cheque</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={payments[student.id]?.adminFee || ''}
                          onChange={(e) => handlePaymentChange(student.id, 'adminFee', e.target.value)}
                          className="fee-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={payments[student.id]?.termFee || ''}
                          onChange={(e) => handlePaymentChange(student.id, 'termFee', e.target.value)}
                          className="fee-input"
                          title="Parents can pay in advance for future terms"
                        />
                      </td>
                    </tr>
                    {/* Payment Breakdown Row */}
                    {expandedRows[student.id] && student.termStructure && (
                      <tr className="term-breakdown-row">
                        <td colSpan={globalSearchQuery.length >= 2 ? 8 : 7} style={{padding: '20px', background: '#f8f9fa'}}>
                          <div style={{
                            background: 'white',
                            padding: '15px',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6'
                          }}>
                            <h4 style={{
                              margin: '0 0 15px 0',
                              color: '#2c3e50',
                              fontSize: '16px',
                              borderBottom: '2px solid #3498db',
                              paddingBottom: '8px'
                            }}>
                              {'\u{1F4CA}'} Payment Breakdown
                            </h4>
                            
                            <div style={{overflowX: 'auto'}}>
                              <div style={{fontSize: '14px', minWidth: '360px'}}>
                              {/* Header Row */}
                              <div style={{
                                display: 'grid', 
                                gridTemplateColumns: '120px 80px 80px 80px',
                                padding: '8px 0',
                                borderBottom: '2px solid #333',
                                fontSize: '12px',
                                fontWeight: '700',
                                color: '#333',
                                backgroundColor: '#f8f9fa'
                              }}>
                                <span></span>
                                <span style={{textAlign: 'right'}}>DUE</span>
                                <span style={{textAlign: 'right'}}>PAID</span>
                                <span style={{textAlign: 'right'}}>BALANCE</span>
                              </div>

                              {/* Admin Fee Row */}
                              <div style={{
                                display: 'grid', 
                                gridTemplateColumns: '120px 80px 80px 80px',
                                padding: '8px 0',
                                borderBottom: '1px solid #ddd',
                                fontWeight: '600',
                                backgroundColor: '#fffbf0'
                              }}>
                                <span><strong>Admin Fee</strong></span>
                                <span style={{textAlign: 'right'}}>
                                  {formatCurrency(student.adminFee || 0)}
                                </span>
                                <span style={{textAlign: 'right', color: (student.termPayments?.admin || 0) > 0 ? '#27ae60' : '#666'}}>
                                  {formatCurrency(student.termPayments?.admin || 0)}
                                </span>
                                <span style={{
                                  textAlign: 'right', 
                                  color: student.adminFeeOwing > 0 ? '#e74c3c' : '#27ae60',
                                  fontWeight: '700'
                                }}>
                                  {formatCurrency(student.adminFeeOwing)}
                                </span>
                              </div>

                              {/* Term Rows */}
                              {calculateTermBreakdown(student, student.termStructure).map(term => {
                                const balance = term.due;
                                return (
                                  <div 
                                    key={term.termNumber}
                                    style={{
                                      display: 'grid', 
                                      gridTemplateColumns: '120px 80px 80px 80px',
                                      padding: '6px 0',
                                      borderBottom: '1px solid #eee',
                                      opacity: term.status !== 'not-due' ? 1 : 0.5
                                    }}
                                  >
                                    <span>
                                      <strong>Term {term.termNumber}</strong>
                                      {term.status === 'not-due' && (
                                        <span style={{fontSize: '11px', color: '#999', marginLeft: '5px'}}>
                                          (not applicable)
                                        </span>
                                      )}
                                    </span>
                                    <span style={{textAlign: 'right'}}>
                                      {formatCurrency(term.termAmount)}
                                    </span>
                                    <span style={{textAlign: 'right', color: term.paid > 0 ? '#27ae60' : '#666'}}>
                                      {formatCurrency(term.paid)}
                                    </span>
                                    <span style={{
                                      textAlign: 'right', 
                                      color: balance > 0 ? '#e74c3c' : (balance < 0 ? '#3498db' : '#27ae60'),
                                      fontWeight: balance !== 0 ? '600' : 'normal'
                                    }}>
                                      {formatCurrency(balance)}
                                    </span>
                                  </div>
                                );
                              })}

                              {/* Total Row */}
                              <div style={{
                                display: 'grid', 
                                gridTemplateColumns: '120px 80px 80px 80px',
                                padding: '10px 0',
                                marginTop: '5px',
                                borderTop: '2px solid #333',
                                fontWeight: '700',
                                fontSize: '15px'
                              }}>
                                <span>TOTAL</span>
                                <span style={{textAlign: 'right'}}>
                                  {formatCurrency(
                                    (student.adminFee || 0) + 
                                    calculateTermBreakdown(student, student.termStructure)
                                      .reduce((sum, t) => sum + (t.status !== 'not-due' ? t.termAmount : 0), 0)
                                  )}
                                </span>
                                <span style={{textAlign: 'right', color: '#27ae60'}}>
                                  {formatCurrency(student.totalPaid || 0)}
                                </span>
                                <span style={{
                                  textAlign: 'right', 
                                  color: student.calculatedOwing > 0 ? '#e74c3c' : '#27ae60',
                                  fontSize: '16px'
                                }}>
                                  {formatCurrency(student.calculatedOwing || 0)}
                                </span>
                              </div>

                              {/* Status Indicators */}
                              <div style={{marginTop: '10px', fontSize: '13px'}}>
                                {student.adminFeeOwing === 0 && (
                                  <p style={{color: '#27ae60', margin: '5px 0'}}>
                                    <strong>{'\u2705'} Admin Fee Fully Paid</strong>
                                  </p>
                                )}
                                {student.creditBalance > 0 && (
                                  <p style={{color: '#3498db', margin: '5px 0'}}>
                                    <strong>{'\u{1F4B0}'} Credit Balance:</strong> {formatCurrency(student.creditBalance)}
                                  </p>
                                )}
                                {student.lastPaymentDate && (
                                  <p style={{color: '#666', margin: '5px 0'}}>
                                    <strong>Last Payment:</strong> {formatDate(student.lastPaymentDate)}
                                  </p>
                                )}
                              </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}

                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bulk-fees-actions">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              marginBottom: '15px',
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                {'\u{1F4C5}'} Payment Date:
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '2px solid #3498db',
                    borderRadius: '5px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                />
              </label>
              <span style={{fontSize: '13px', color: '#7f8c8d'}}>
                This date will be used for all payments entered above
              </span>
            </div>
            <button
              onClick={handleSaveAll}
              disabled={saving}
              className="btn-save-all"
            >
              {saving ? `${'\u{1F4BE}'} Saving...` : `${'\u{1F4BE}'} Save All Payments`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default AddBulkFees;